import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import * as Sentry from '@sentry/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import i18n from './i18next';

import App from './App';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import {
  scrubBreadcrumb,
  scrubEvent,
  scrubTransaction,
} from './lib/sentryScrub';

const surface = window.location.pathname.startsWith('/patient-form')
  ? 'patient-form-webview'
  : 'backoffice';

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  environment: process.env.SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  sampleRate: 1.0,
  tracesSampleRate: 0.2,
  sendDefaultPii: false,
  beforeSend: scrubEvent,
  beforeSendTransaction: scrubTransaction,
  beforeBreadcrumb: scrubBreadcrumb,
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
  ],
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
  ],
  initialScope: { tags: { surface } },
});

const emotionInsertionPoint = document.createElement('meta');
emotionInsertionPoint.setAttribute('name', 'emotion-insertion-point');
document.head.appendChild(emotionInsertionPoint);
const emotionCache = createCache({
  key: 'css',
  insertionPoint: emotionInsertionPoint,
});

createRoot(document.getElementById('app')!).render(
  <CacheProvider value={emotionCache}>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
          <App />
        </Sentry.ErrorBoundary>
      </BrowserRouter>
    </I18nextProvider>
  </CacheProvider>
);
